import {
  BankIncomeSource,
  InstitutionMetadata,
  PayrollEmployment,
  SelfReportedIncomeSource,
  SelfReportedVerificationItem
} from "types/landlord/reports";
import { formatToCurrency } from "utils/currency";
import {
  formatDate,
  formatEnumValue
} from "views/applications-id-report/utils";
import { PayrollIncome } from "./types";
import { formatPhone } from "shared/utils";
import moment from "moment";
import { getHumanReadableDateDiff } from "views/applications-id-report/helper";
import {
  BankStatementIncomeSource,
  BankStatementPeriod,
  W2RawFields
} from "../../types";
import { PayStubI } from "views/applications-id-report/utils/PayStub";
import {
  Bank,
  BriefCase01,
  Laptop01,
  Mail05,
  PiggyBank01
} from "untitledui-js-base";

export const getW2Mappings = (data: W2RawFields) => {
  const employerInformationMappings = [
    {
      key: "Employer ID no.",
      value: data.employerIdNo ? String(data.employerIdNo.value) : "-"
    },
    { key: "Employer name", value: data.employerName?.value || "-" },
    {
      key: "Employer address state",
      value: data.employerAddressState?.value || "-"
    },
    {
      key: "Employer address city",
      value: data.employerAddressCity?.value || "-"
    },
    {
      key: "Employer address zip",
      value: String(data.employerAddressZip?.value || "-")
    },
    {
      key: "Employer address line1",
      value: data.employerAddressLine1?.value || "-"
    },
    {
      key: "Employer address line2",
      value: data.employerAddressLine2?.value || "-"
    },
    {
      key: "Employer state no. primary",
      value: data.employerStateIdNumberPrimary?.value || "-"
    }
  ];

  const employeeInformationMappings = [
    { key: "Employee name", value: data.employeeName?.value || "-" },
    {
      key: "Employee address state",
      value: data.employeeAddressState?.value || "-"
    },
    {
      key: "Employee address city",
      value: data.employeeAddressCity?.value || "-"
    },
    {
      key: "Employee address zip",
      value: String(data.employeeAddressZip.value)
    },
    {
      key: "Employee address line1",
      value: data.employeeAddressLine1?.value || "-"
    },
    {
      key: "Employee address line2",
      value: data.employeeAddressLine2?.value || "-"
    },
    { key: "Statutory employee", value: data.statutoryEmployee?.value || "-" },
    {
      key: "Social security number",
      value: String(data.employeeSocialSecurityNumber?.value || "-")
    }
  ];

  const additionalInformationMappings = [
    { key: "Box9", value: data.box9?.value || "-" },
    { key: "Box12 a code", value: data.box12aCode?.value || "-" },
    { key: "Box12 b code", value: data.box12bCode?.value || "-" },
    { key: "Box12 c code", value: data.box12cCode?.value || "-" },
    { key: "Box12 d code", value: data.box12dcode?.value || "-" },
    { key: "Box14 other", value: data.box14Other?.value || "-" },
    {
      key: "Box12 a amount",
      value: data.box12aAmount?.value
        ? formatToCurrency(data.box12aAmount.value)
        : "-"
    },
    {
      key: "Box12 b amount",
      value: data.box12bAmount?.value
        ? formatToCurrency(data.box12bAmount.value)
        : "-"
    },
    {
      key: "Box12 c amount",
      value: data.box12cAmount?.value
        ? formatToCurrency(data.box12cAmount.value)
        : "-"
    },
    {
      key: "Box12 d amount",
      value: data.box12dAmount?.value
        ? formatToCurrency(data.box12dAmount.value)
        : "-"
    },
    {
      key: "Qualified plans",
      value: data.qualifiedPlans?.value
        ? formatToCurrency(data.qualifiedPlans.value)
        : "-"
    },
    { key: "Retirement plan", value: data.retirementPlan?.value || "-" },
    { key: "State primary", value: data.statePrimary?.value || "-" },
    { key: "State secondary", value: data.stateSecondary?.value || "-" },
    {
      key: "Third-party sick pay",
      value: data["third-PartySickPay"]?.value || "-"
    },
    {
      key: "Locality name primary",
      value: data.localityNamePrimary?.value || "-"
    },
    {
      key: "Dependent care benefits",
      value: data.dependentCareBenefits?.value
        ? formatToCurrency(data.dependentCareBenefits.value)
        : "-"
    }
  ];

  const compensationAndTaxInfoMappings = [
    { key: "Year", value: String(data.year?.value) },
    {
      key: "Wages tips other comp",
      value: data.wagesTipsOtherComp?.value
        ? formatToCurrency(data.wagesTipsOtherComp.value)
        : "-"
    },
    {
      key: "Social security wages",
      value: data.socialSecurityWages?.value
        ? formatToCurrency(data.socialSecurityWages.value)
        : "-"
    },
    {
      key: "Medicare wages and tips",
      value: data.medicareWagesAndTips?.value
        ? formatToCurrency(data.medicareWagesAndTips.value)
        : "-"
    },
    {
      key: "Medicare tax withheld",
      value: data.medicareTaxWithheld?.value
        ? formatToCurrency(data.medicareTaxWithheld.value)
        : "-"
    },
    {
      key: "Social security tax withheld",
      value: data.medicareTaxWithheld?.value
        ? formatToCurrency(data.socialSecurityTaxWithheld.value)
        : "-"
    },
    {
      key: "Social security tips",
      value: data.socialSecurityTips?.value
        ? formatToCurrency(data.socialSecurityTips.value)
        : "-"
    },
    {
      key: "Allocated tips",
      value: data.allocatedTips?.value
        ? formatToCurrency(data.allocatedTips.value)
        : "-"
    },
    {
      key: "Federal income tax withheld",
      value: data.federalIncomeTaxWithheld?.value
        ? formatToCurrency(data.federalIncomeTaxWithheld.value)
        : "-"
    },
    {
      key: "State income tax primary",
      value: data.stateIncomeTaxPrimary?.value
        ? formatToCurrency(data.stateIncomeTaxPrimary.value)
        : "-"
    },
    {
      key: "Local wages tips primary",
      value: data.localWagesTipsPrimary?.value
        ? formatToCurrency(data.localWagesTipsPrimary.value)
        : "-"
    },
    {
      key: "Locality name primary",
      value: data.localityNamePrimary?.value || "-"
    },
    {
      key: "Local wages tips secondary",
      value: data.localWagesTipsSecondary?.value
        ? data.localWagesTipsSecondary.value
        : "-"
    },
    {
      key: "Locality name secondary",
      value: data.localityNameSecondary?.value || "-"
    },
    {
      key: "State wages tips primary",
      value: data.stateWagesTipsPrimary?.value
        ? formatToCurrency(data.stateWagesTipsPrimary.value)
        : "-"
    },
    {
      key: "State wages tips secondary",
      value: data.stateWagesTipsSecondary?.value
        ? formatToCurrency(data.stateWagesTipsSecondary.value)
        : "-"
    },
    {
      key: "State income tax secondary",
      value: data.stateIncomeTaxSecondary?.value
        ? formatToCurrency(data.stateIncomeTaxSecondary.value)
        : "-"
    }
  ];

  return {
    employerInformationMappings,
    employeeInformationMappings,
    additionalInformationMappings,
    compensationAndTaxInfoMappings
  };
};

export const getPayrollMappings = (
  employment: PayrollEmployment[],
  income: PayrollIncome
) => {
  const incomeEmployment: PayrollEmployment | undefined =
    employment.find((emp) => emp.account_id === income.account_id) ||
    (employment[0] as PayrollEmployment | undefined);

  const payrollEmploymentDetailsMappings = [
    {
      key: "Company name",
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      value: incomeEmployment?.employer?.name
    },
    {
      key: "Company address",
      value: incomeEmployment?.address
        ? `${incomeEmployment.address.street}, ${incomeEmployment.address.city}, ${incomeEmployment.address.region}`
        : "-"
    },
    {
      key: "Job title",
      value: incomeEmployment?.title
    },
    {
      key: "Status",
      value: incomeEmployment?.status
    },
    {
      key: "Start date",
      value: formatDate(incomeEmployment?.start_date)
    },
    {
      key: "Employment type",
      value: formatEnumValue(incomeEmployment?.employee_type || "")
    },
    {
      key: "Gross annual income",
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      value: income.gross_monthly_income
        ? formatToCurrency({
            ...income.gross_monthly_income,
            cents: income.gross_monthly_income.cents * 12
          })
        : "-"
    },
    {
      key: "Net annual income",
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      value: income.net_monthly_income
        ? formatToCurrency({
            ...income.net_monthly_income,
            cents: income.net_monthly_income.cents * 12
          })
        : "-"
    },
    {
      key: "Pay frequency",
      value: income.account?.pay_frequency
        ? formatEnumValue(income.account.pay_frequency)
        : "-"
    }
  ];

  const payrollIncomeSummaryMappings = [
    {
      key: "Income start date",
      value: formatDate(income.start_date)
    },
    {
      key: "Income end date",
      value: formatDate(income.end_date)
    },
    {
      key: "Total net earnings",
      value: formatToCurrency(income.total_net)
    }
  ];

  return {
    payrollEmploymentDetailsMappings,
    payrollIncomeSummaryMappings
  };
};

export const getPayStubsMappings = (data: PayStubI) => {
  const payStubsDetailsMappings = [
    {
      key: "Pay period",
      value: data.paystub_details.pay_period_end_date
        ? `${moment(data.paystub_details.pay_period_start_date).format(
            "MMMM D, YYYY"
          )} - ${moment(data.paystub_details.pay_period_end_date).format("MMMM D, YYYY")}`
        : "-"
    },
    {
      key: "Pay date",
      value: data.paystub_details.pay_date
        ? moment(data.paystub_details.pay_date).format("MMMM D, YYYY")
        : "-"
    },
    {
      key: "Net pay",
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      value: data.total_net_pay ? formatToCurrency(data.total_net_pay) : "-"
    },
    {
      key: "Gross pay",
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      value: data.total_gross_pay ? formatToCurrency(data.total_gross_pay) : "-"
    },
    { key: "Provider", value: data.paystub_details.paystub_provider },
    { key: "Frequency", value: data.paystub_details.pay_frequency }
  ];

  const employerInformationMappings = [
    { key: "Name", value: data.employer.name },
    {
      key: "Address line 1",
      value: data.employer.address.line1
    },
    {
      key: "Address line 2",
      value: data.employer.address.line2
    },
    { key: "City", value: data.employer.address.city },
    { key: "State", value: data.employer.address.state_code },
    { key: "Zip", value: data.employer.address.postal_code }
  ];

  const employeeInformationMappings = [
    {
      key: "Name",
      value: data.employee.name
    },
    { key: "Address line 1", value: data.employee.address.line1 },
    { key: "Address line 2", value: data.employee.address.line2 },
    { key: "City", value: data.employee.address.city },
    { key: "State", value: data.employee.address.state_code },
    { key: "Zip", value: data.employee.address.postal_code },
    { key: "Marital status", value: data.employee.marital_status },
    { key: "Tax ID Type", value: data.employee.taxpayer_id.id_type },
    { key: "Last 4 Digits", value: data.employee.taxpayer_id.last_4_digits }
  ];

  return {
    payStubsDetailsMappings,
    employerInformationMappings,
    employeeInformationMappings
  };
};

export const getBankAccountMappings = (
  data?: BankIncomeSource,
  institution?: InstitutionMetadata
) => {
  const bankIncomeSourceMappings = [
    {
      key: "Income description",
      value: data?.income_description || "-"
    },
    {
      key: "Income category",
      value: data?.income_category || "-"
    },
    {
      key: "Employer name",
      value: data?.employer?.name || "-"
    },
    {
      key: "Income start date",
      value: formatDate(data?.start_date)
    },
    {
      key: "Income end date",
      value: formatDate(data?.end_date)
    },
    {
      key: "Pay frequency",
      value: data?.pay_frequency || "-"
    },
    {
      key: "Transaction count",
      value: String(data?.transaction_count) || "-"
    },
    {
      key: "Monthly net income",
      value: data?.net_monthly_income
        ? formatToCurrency(data.net_monthly_income)
        : "-"
    },
    {
      key: "Monthly gross income",
      value: data?.gross_monthly_income
        ? formatToCurrency(data.gross_monthly_income)
        : "-"
    },
    {
      key: `Forecasted monthly net${"\n"}income`,
      value: data?.forecasted_net_monthly_income
        ? formatToCurrency(data.forecasted_net_monthly_income)
        : "-"
    },
    {
      key: "Next payment date",
      value: formatDate(data?.next_payment_date)
    }
  ];
  const bankAccountMappings = [
    {
      key: "Account ID",
      value: String(data?.income_source_id)
    },
    {
      key: "Bank account",
      value: data?.name || "-"
    },
    {
      key: "Institution",
      value: institution?.name || "-"
    },
    {
      key: "Source bank account ",
      value: data?.name || "-"
    },
    {
      key: "Names",
      value: data?.owners.map((owner) => owner.names).join(",") || "-"
    },
    {
      key: "Phone numbers",
      value:
        data?.owners
          .flatMap((owner) => owner.phone_numbers)
          .map((pn) => `${formatPhone(pn.data)} ${pn.type}`)
          .join("\n") || "-"
    },
    {
      key: "Emails",
      value:
        data?.owners
          .flatMap((owner) => owner.emails)
          .map((email) => email.data)
          .join(", ") || "-"
    },
    {
      key: "Account holder address",
      value:
        data?.owners
          .flatMap((owner) => owner.addresses)
          .map((address) => address.data.street)
          .join("\n") || "-"
    }
  ];
  return {
    bankAccountMappings,
    bankIncomeSourceMappings
  };
};

export const getBankStatementDetailsMappings = (
  data: BankStatementIncomeSource
) => {
  return [
    {
      key: "Account name",
      value: data.name
    },
    { key: "Account number", value: String(data.account_number) },
    { key: "Type", value: data.account_type },
    { key: "Holder", value: data.account_holder },
    { key: "Category", value: data.account_category },
    { key: "Holder address line 1", value: data.holder_address_1 },
    {
      key: "Holder address line 2",
      value: data.holder_address_2 ? data.holder_address_2 : "-"
    },
    { key: "Holder zip", value: String(data.holder_zip) },
    { key: "Holder city", value: data.holder_city },
    { key: "Holder state", value: data.holder_state }
  ];
};

export const getBankStatementIncomeSummaryMappings = (
  data?: BankStatementPeriod
) => {
  return [
    {
      key: "Income start date",
      value: formatDate(data?.begin_date)
    },
    { key: "Income end date", value: formatDate(data?.end_date) },
    {
      key: "Starting balance",
      value: data?.begin_balance ? formatToCurrency(data.begin_balance) : "-"
    },
    {
      key: "Ending balance",
      value: data?.end_balance ? formatToCurrency(data.end_balance) : "-"
    },
    {
      key: "Average daily balance",
      value: data?.average_daily_balance
        ? formatToCurrency(data.average_daily_balance)
        : "-"
    }
  ];
};

export const getSelfReportedIncomeMappings = (
  data: SelfReportedVerificationItem
) => {
  const currentTypeOfSelfEmployment = data.income_source
    .toLowerCase()
    .replaceAll(/[ -]+/g, "_");

  const preTaxIncomeMappings = [
    {
      key: data[currentTypeOfSelfEmployment]?.pre_tax_income.income_type,
      value: data[currentTypeOfSelfEmployment].pre_tax_income.income_amount
        ? formatToCurrency({
            cents:
              data[currentTypeOfSelfEmployment].pre_tax_income.income_amount *
              100,
            currency: "USD"
          })
        : "-"
    }
  ];
  const supervisorMappings = [
    {
      key: "Name",
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      value: `${data?.employment?.supervisor?.first_name || data?.offer_letter?.supervisor?.first_name} ${data?.employment?.supervisor?.last_name || data?.offer_letter?.supervisor?.last_name}`
    },
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ...(data?.employment?.supervisor?.supervisor_email ||
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    data?.offer_letter?.supervisor?.supervisor_email
      ? [
          {
            key: "Email",
            value:
              data.employment?.supervisor.supervisor_email ||
              data.offer_letter?.supervisor?.supervisor_email ||
              "-"
          }
        ]
      : []),
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ...(data?.employment?.supervisor?.supervisor_phone ||
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    data?.offer_letter?.supervisor?.supervisor_phone
      ? [
          {
            key: "Phone",
            value:
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              data?.employment?.supervisor?.supervisor_phone ||
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              data?.offer_letter?.supervisor?.supervisor_phone ||
              "-"
          }
        ]
      : [])
  ];

  const incomeMappings = [
    {
      key: "Income type",
      value: data.other_income?.income_description || "-"
    },
    {
      key: "Paid by / from",
      value: data.other_income?.paid_by || "-"
    },
    {
      key: "Start date",
      value: data.other_income?.start_date
        ? moment(data.other_income.start_date).format("MMM DD, YYYY")
        : "-"
    },
    {
      key: "End date",
      value: data.other_income?.end_date
        ? moment(data.other_income.end_date).format("MMM DD, YYYY")
        : "-"
    }
  ];

  const employmentDetailsMappings = [
    {
      key: "Company name",
      value: data[currentTypeOfSelfEmployment]?.company_name
    },
    { key: "Job title", value: data[currentTypeOfSelfEmployment]?.job_title },
    {
      key: "Start date",
      value: data[currentTypeOfSelfEmployment]?.start_date
        ? moment(data[currentTypeOfSelfEmployment]?.start_date).format(
            "MMM DD, YYYY"
          )
        : "-"
    },
    ...(data.income_source !== SelfReportedIncomeSource.OfferLetter
      ? [
          {
            key: "End date",
            value: data[currentTypeOfSelfEmployment]?.end_date
              ? moment(data[currentTypeOfSelfEmployment].end_date).format(
                  "MMM DD, YYYY"
                )
              : "-"
          }
        ]
      : [])
  ];

  return {
    preTaxIncomeMappings,
    incomeMappings,
    supervisorMappings,
    employmentDetailsMappings
  };
};

export const getIncomeSourceInfo = (data: any) => {
  if (data.income_source_type === "bank")
    return {
      title: data.income_description,
      subtitle: data.income_category,
      icon: BriefCase01
    };
  if (data.income_source_type === "payroll")
    return {
      title: data.institution_name,
      subtitle: "Employment",
      icon: BriefCase01
    };
  if (data.docType === "w2")
    return {
      title: data.employer_name,
      subtitle: data.income_category || "Employment",
      icon: BriefCase01
    };

  if (data.docType === "bank_statement")
    return {
      title: data.bank_name,
      subtitle: "Bank account",
      icon: Bank
    };

  if (data.docType === "pay_stub")
    return {
      title: data.employer,
      subtitle: "Salary",
      icon: BriefCase01
    };

  return { title: "Unknown", subtitle: "", icon: BriefCase01 };
};

export const getSelfAttestedIncomeSourceInfo = (data: any) => {
  if (data.income_source === SelfReportedIncomeSource.OtherIncome)
    return {
      title: data.other_income?.income_description,
      subtitle: "Other income",
      icon: PiggyBank01
    };
  if (data.income_source === SelfReportedIncomeSource.OfferLetter)
    return {
      title: data.offer_letter?.company_name,
      subtitle: "Offer letter",
      icon: Mail05
    };
  if (data.income_source === SelfReportedIncomeSource.SelfEmployed)
    return {
      title: data.self_employed?.company_name,
      subtitle: "Self-employment",
      icon: Laptop01
    };
  if (data.income_source === SelfReportedIncomeSource.Employment)
    return {
      title: data.employment?.company_name,
      subtitle: "Employment",
      icon: BriefCase01
    };
  return { title: "Unknown", subtitle: "", icon: BriefCase01 };
};

export const getDateRangeAndDiffByDocType = (data) => {
  const docType = data?.docType;
  let startDate, endDate;

  if (docType === "bank_statement") {
    startDate = data?.periods?.[0]?.begin_date;
    endDate = data?.periods?.[data?.periods?.length - 1]?.end_date;
  } else {
    startDate = data?.start_date;
    endDate = data?.end_date;
  }

  const formattedStartDate = startDate
    ? moment(startDate).format("MMM DD, YYYY")
    : "-";
  const formattedEndDate = endDate
    ? moment(endDate).format("MMM DD, YYYY")
    : "-";
  const dateRange = `${formattedStartDate} - ${formattedEndDate}`;

  const humanReadableDiff =
    startDate && endDate
      ? `${getHumanReadableDateDiff(startDate, endDate)}`
      : "-";

  return { dateRange, humanReadableDiff };
};
